import { observer } from 'mobx-react-lite'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { match } from '@/utils/pattern-match-util'
import { type RequestScopeChangesDialogViewModel } from './RequestScopeChangesDialogViewModel'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import { ScopeChangeInventoryItemContainer } from '@/features/jobs/details/components/RequestScopeChangesDialog/ListItems/ScopeChangeInventoryItem/ScopeChangeInventoryItemContainer'
import { ScopeChangeCustomItemContainer } from '@/features/jobs/details/components/RequestScopeChangesDialog/ListItems/ScopeChangeCustomItem/ScopeChangeCustomItemContainer'
import {
  RequestScopeChangesDialog,
  RequestScopeChangesFailedStep,
  RequestScopeChangesQuantityStep,
  RequestScopeChangesReviewStep,
  RequestScopeChangesSelectionStep,
  RequestScopeChangesSuccessStep,
} from '@/features/jobs/details/components/RequestScopeChangesDialog'

export interface RequestScopeChangesDialogContainerProps {
  vm: RequestScopeChangesDialogViewModel
}

export const RequestScopeChangesDialogContainer = observer(
  function RequestScopeChangesDialogContainer({
    vm,
  }: RequestScopeChangesDialogContainerProps) {
    return (
      <RequestScopeChangesDialog
        showing={vm.modal.showing}
        state={vm.state}
        isSubmitPending={vm.request.pending}
        onClose={vm.modal.close}
      >
        {match(vm.state, {
          RequestingChanges: () => (
            <RequestScopeChangesSelectionStep
              searchText={vm.searchText.value}
              onSearchTextChange={vm.searchText.change}
              showAddCustomItemEmptyState={vm.canShowAddCustomItemEmptyState}
              canCreateCustomItem={vm.canCreateCustomJobScopeItemRequest}
              onAddCustomItem={vm.addScopeChange}
              totalSelectedInventoryItemsCount={
                vm.totalSelectedInventoryItemsCount
              }
              isNextDisabled={!vm.itemsSelected}
              onNext={vm.moveToQuantityStep}
              searchFieldTestId="request-scope-changes-search-field"
              addButtonTestId="request-scope-changes-add-button"
              nextButtonTestId="request-scope-changes-next-button"
            >
              {vm.serviceItems.map((item) => {
                return (
                  <ScopeChangeInventoryItemContainer
                    key={item.versionId}
                    vm={item}
                  />
                )
              })}
              {vm.scopeChangeCustomItems.map((item, index) => {
                return (
                  <ScopeChangeCustomItemContainer
                    key={item.key}
                    vm={item}
                    onRemove={vm.removeScopeChange}
                    position={index}
                  />
                )
              })}
              <InfiniteScrollLoaderContainer
                compact
                scrollProvider={vm.inventoryScrollProvider}
              />
            </RequestScopeChangesSelectionStep>
          ),
          UpdatingQuantity: () => (
            <RequestScopeChangesQuantityStep
              totalSelectedInventoryItemsCount={
                vm.totalSelectedInventoryItemsCount
              }
              isNextDisabled={!vm.quantitiesChanged}
              onBack={vm.backToSelectionStep}
              onNext={vm.moveToSubmissionStep}
              nextButtonTestId="request-scope-changes-next-button"
            >
              {vm.selectedInventoryItems.map((item) => {
                return (
                  <ScopeChangeInventoryItemContainer
                    updateQuantity
                    key={item.versionId}
                    vm={item}
                  />
                )
              })}
              {vm.scopeChangeCustomItems.map((item, index) => {
                return (
                  <ScopeChangeCustomItemContainer
                    updateQuantity
                    key={item.key}
                    vm={item}
                    onRemove={vm.removeScopeChange}
                    position={index}
                  />
                )
              })}
            </RequestScopeChangesQuantityStep>
          ),
          ReviewingChanges: () => (
            <RequestScopeChangesReviewStep
              inventoryItems={vm.relevantSelectedInventoryItems.map((item) => ({
                versionId: item.versionId,
                name: item.name,
                description: item.description,
                newQuantity: item.newQuantity,
              }))}
              customItems={vm.scopeChangeCustomItems.map((item) => ({
                key: item.key,
                name: item.name,
                quantity: item.quantity,
              }))}
              reasonField={
                <TextAreaContainer
                  label="Reason"
                  model={vm}
                  field="reason"
                  name="reason"
                  data-test="request-scope-changes-reason-input"
                  help={`${vm.reasonCharsRemaining} characters remaining.`}
                  error={vm.submissionValidationStep.getError('reason')}
                  focusOnError
                />
              }
              isDisabled={vm.request.pending}
              isSubmitPending={vm.request.pending}
              onBack={vm.backToQuantityStep}
              onSubmit={vm.request}
              submitButtonTestId="request-scope-changes-submit-button"
            />
          ),
          Success: () => (
            <RequestScopeChangesSuccessStep
              onClose={vm.modal.close}
              titleTestId="request-scope-changes-success"
            />
          ),
          Failed: () => (
            <RequestScopeChangesFailedStep
              error={vm.error}
              isDisabled={vm.request.pending}
              onClose={vm.modal.close}
            />
          ),
        })}
      </RequestScopeChangesDialog>
    )
  },
)
